
import { supabase } from '../supabaseClient';
import { TestSet, Question } from '../types/models';
import { questionService } from './questionService';

type TestSetFromDB = {
  id: string;
  name: string;
  questions_ids: string[];
  is_published: boolean;
};

// Funkcja pomocnicza do mapowania danych z bazy na nasz model
const mapTestSetFromDB = (set: TestSetFromDB, allQuestions: Question[]): TestSet => {
  const questionsForSet: Question[] = set.questions_ids
    .map(id => allQuestions.find(q => q.id === id))
    .filter((q): q is Question => q !== undefined);

  return {
    id: set.id,
    name: set.name,
    questions: questionsForSet,
    isPublished: set.is_published,
  };
};

export const testSetService = {
  // Usuwanie zestawu pytań
  deleteTestSet: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('test_sets')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  },
  // Aktualizacja zestawu pytań
  updateTestSet: async (id: string, data: { name?: string; questions_ids?: string[]; is_published?: boolean }): Promise<TestSet> => {
    const { data: updated, error } = await supabase
      .from('test_sets')
      .update(data)
      .eq('id', id)
      .select('id, name, questions_ids, is_published')
      .single();
    if (error) throw new Error(error.message);
    if (!updated) throw new Error('Nie udało się zaktualizować zestawu.');
    const allQuestions = await questionService.getAllQuestions();
    return mapTestSetFromDB(updated, allQuestions);
  },
  // Pobiera WSZYSTKIE zestawy (dla instruktora)
  getAllTestSets: async (): Promise<TestSet[]> => {
    const { data: setsFromDB, error: setsError } = await supabase
      .from('test_sets')
      .select('id, name, questions_ids, is_published');

    if (setsError) throw new Error(setsError.message);
    if (!setsFromDB) return [];

    const allQuestions = await questionService.getAllQuestions();
    
    return setsFromDB.map(set => mapTestSetFromDB(set, allQuestions));
  },

  // Pobiera TYLKO OPUBLIKOWANE zestawy (dla kursanta)
  getPublishedTestSets: async (): Promise<TestSet[]> => {
    const { data: setsFromDB, error: setsError } = await supabase
      .from('test_sets')
      .select('id, name, questions_ids, is_published')
      .eq('is_published', true);

    if (setsError) throw new Error(setsError.message);
    if (!setsFromDB) return [];

    const allQuestions = await questionService.getAllQuestions();

    return setsFromDB.map(set => mapTestSetFromDB(set, allQuestions));
  },

  // Zmienia status publikacji zestawu
  toggleTestSetPublication: async (setId: string, newStatus: boolean): Promise<TestSet> => {
    const { data, error } = await supabase
      .from('test_sets')
      .update({ is_published: newStatus })
      .eq('id', setId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error("Nie udało się zaktualizować zestawu.");

    const allQuestions = await questionService.getAllQuestions();
    return mapTestSetFromDB(data, allQuestions);
  },

  createTestSet: async (name: string, questionIds: string[]): Promise<TestSet> => {
    const { data, error } = await supabase
      .from('test_sets')
      .insert({ name: name, questions_ids: questionIds })
      .select('id, name, questions_ids, is_published')
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error("Nie udało się pobrać danych po utworzeniu zestawu.");

    const allQuestions = await questionService.getAllQuestions();
    return mapTestSetFromDB(data, allQuestions);
  },
  
  getTestSetById: async (id: string): Promise<TestSet | undefined> => {
    const { data: setFromDB, error } = await supabase
      .from('test_sets')
      .select('id, name, questions_ids, is_published')
      .eq('id', id)
      .single();

    if (error || !setFromDB) {
      console.error('Błąd podczas pobierania zestawu po ID:', error);
      return undefined;
    }
    
    const { data: questionsData, error: questionsError } = await supabase
      .from('questions')
      .select('*')
      .in('id', setFromDB.questions_ids);

    if (questionsError) {
        console.error('Błąd podczas pobierania pytań dla zestawu:', questionsError);
        return undefined;
    }

    const questions: Question[] = questionsData.map(q => ({
        id: q.id,
        text: q.text,
        optionA: q.optionA,
        optionB: q.optionB,
        optionC: q.optionC,
        correctOptions: q.correct_options || [],
        imageUrl: q.image_url,
    }));

    return {
        id: setFromDB.id,
        name: setFromDB.name,
        questions: questions,
        isPublished: setFromDB.is_published
    };
  },
};